import React, { useCallback, useEffect, useState } from 'react';
import { GridContainer, Grid } from 'src/shared/grid';
import { Formik, Form } from 'formik';
import { Autocomplete, Box, Chip, Divider, IconButton, MenuItem, Modal, OutlinedInput, Select, SelectChangeEvent, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import 'src/features/field-sample//FieldSample.scss';
import * as Yup from 'yup';
import { useAddAlert } from 'src/store/actions';
import { useStoreContext } from 'src/store/context';
import { useHistory, useLocation } from 'react-router-dom';
import { alertMessages, FuelCatalogBasedOnModels, FuelModels, GrassTypeOptions } from 'src/constants';
import { errorResponse, validateCharactersLength, validateInt, ValidCharacters } from 'src/store/common';
import CloseIcon from '@mui/icons-material/Close';
import { ROUTE_PATHS } from 'src/routes';
import { modalStyle } from 'src/shared/Styles/Styles';
import { AddDroughtCatalog } from './AddDroughtCatalog';
import { AddLiveCatalog } from './AddLiveCatalog';
import { AddDeadCatalog } from './AddDeadCatalog';
import { getDeadCatalogsCall, getDroughtCatalogCall, getFuelCatalogs, getLiveCatalogCall } from 'src/apis/getCatalogCalls';
import { createUpdateFuelModelCatalogCall } from 'src/apis/mutationCatalogs';
import LoadingSpinner from 'src/shared/loading/loading';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const validationSchema = Yup.object().shape(
  {
    fuelCatalogName: Yup.string().nullable()
      .required('Fuel Model Catalog Name is Required'),
    fuel_model: Yup.string().nullable()
      .required('Fuel Model is Required'),
    fuel_adjust: Yup.string().nullable(),
    drought_id: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.length>0 && !fuel_adjust?.includes('Drought Catalog'),
        then: Yup.string().nullable()
          .required('Drought Catalog is Required')
    }),
    dead_id: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.length>0 && !fuel_adjust?.includes('Dead Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Dead Fuel Catalog is Required')
    }),
    live_catalog_id: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.length>0 && !fuel_adjust?.includes('Live Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Live Fuel Catalog is Required')
    }),

    
    mxd_override_v: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('V'),
        then: Yup.number()
          .min(0, "MXD Override must be greater than or equal to 0")
          .max(100, "MXD Override must be less than or equal to 100")
          .required('MXD Override (Fuel Model V) is Required'),
      }),
    mxd_override_w: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('W'),
        then: Yup.number()
          .min(0, "MXD Override must be greater than or equal to 0")
          .max(100, "MXD Override must be less than or equal to 100")
          .required('MXD Override (Fuel Model W) is Required'),
      }),
    mxd_override_x: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('X'),
        then: Yup.number()
          .min(0, "MXD Override must be greater than or equal to 0")
          .max(100, "MXD Override must be less than or equal to 100")
          .required('MXD Override (Fuel Model X) is Required'),
      }),
    mxd_override_y: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('Y'),
        then: Yup.number()
          .min(0, "MXD Override must be greater than or equal to 0")
          .max(100, "MXD Override must be less than or equal to 100")
          .required('MXD Override (Fuel Model Y) is Required'),
      }),
    mxd_override_z: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('Z'),
        then: Yup.number()
          .min(0, "MXD Override must be greater than or equal to 0")
          .max(100, "MXD Override must be less than or equal to 100")
          .required('MXD Override (Fuel Model Z) is Required'),
      }),

    scm_v: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('V'),
        then: Yup.number()
          .min(0, "SCM must be greater than or equal to 0")
          .max(1000, "SCM must be less than or equal to 1000")
          .required('SCM (Fuel Model V) is Required'),
    }),
    scm_w: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('W'),
        then: Yup.number()
          .min(0, "SCM must be greater than or equal to 0")
          .max(1000, "SCM must be less than or equal to 1000")
          .required('SCM (Fuel Model W) is Required'),
    }),
    scm_x: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('X'),
        then: Yup.number()
          .min(0, "SCM must be greater than or equal to 0")
          .max(1000, "SCM must be less than or equal to 1000")
          .required('SCM (Fuel Model X) is Required'),
    }),
    scm_y: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('Y'),
        then: Yup.number()
          .min(0, "SCM must be greater than or equal to 0")
          .max(1000, "SCM must be less than or equal to 1000")
          .required('SCM (Fuel Model Y) is Required'),
    }),
    scm_z: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('Z'),
        then: Yup.number()
          .min(0, "SCM must be greater than or equal to 0")
          .max(1000, "SCM must be less than or equal to 1000")
          .required('SCM (Fuel Model Z) is Required'),
    }),
    slope_class_v: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('V'),
        then: Yup.number()
          .min(0, "Slope Class must be greater than or equal to 0")
          .max(5, "Slope Class must be less than or equal to 5")
          .required('Slope Class (Fuel Model V) is Required'),
    }),
    slope_class_w: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('W'),
        then: Yup.number()
          .min(0, "Slope Class must be greater than or equal to 0")
          .max(5, "Slope Class must be less than or equal to 5")
          .required('Slope Class (Fuel Model W) is Required'),
    }),
    slope_class_x: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('X'),
        then: Yup.number()
          .min(0, "Slope Class must be greater than or equal to 0")
          .max(5, "Slope Class must be less than or equal to 5")
          .required('Slope Class (Fuel Model X) is Required'),
    }),
    slope_class_y: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('Y'),
        then: Yup.number()
          .min(0, "Slope Class must be greater than or equal to 0")
          .max(5, "Slope Class must be less than or equal to 5")
          .required('Slope Class (Fuel Model Y) is Required'),
    }),
    slope_class_z: Yup.number().nullable()
      .when('fuel_model', {
        is: (fuel_model) => fuel_model?.includes('Z'),
        then: Yup.number()
          .min(0, "Slope Class must be greater than or equal to 0")
          .max(5, "Slope Class must be less than or equal to 5")
          .required('Slope Class (Fuel Model Z) is Required'),
    }),

    drought_id_v: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('V') && fuel_adjust?.includes('Drought Catalog'),
        then: Yup.string().nullable()
          .required('Drought Catalog (Fuel Model W) is Required')
    }),
    drought_id_w: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('W') && fuel_adjust?.includes('Drought Catalog'),
        then: Yup.string().nullable()
          .required('Drought Catalog (Fuel Model W) is Required')
    }),
    drought_id_x: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('X') && fuel_adjust?.includes('Drought Catalog'),
        then: Yup.string().nullable()
          .required('Drought Catalog (Fuel Model X) is Required')
    }),
    drought_id_y: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('Y') && fuel_adjust?.includes('Drought Catalog'),
        then: Yup.string().nullable()
          .required('Drought Catalog (Fuel Model Y) is Required')
    }),
    drought_id_z: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('Z') && fuel_adjust?.includes('Drought Catalog'),
        then: Yup.string().nullable()
          .required('Drought Catalog (Fuel Model Z) is Required')
    }),


    dead_id_v: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('V') && fuel_adjust?.includes('Dead Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Dead Fuel Catalog (Fuel Model V) is Required')
    }),
    dead_id_w: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('W') && fuel_adjust?.includes('Dead Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Dead Fuel Catalog (Fuel Model W) is Required')
    }),
    dead_id_x: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('X') && fuel_adjust?.includes('Dead Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Dead Fuel Catalog (Fuel Model X) is Required')
    }),
    dead_id_y: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('Y') && fuel_adjust?.includes('Dead Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Dead Fuel Catalog (Fuel Model Y) is Required')
    }),
    dead_id_z: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('Z') && fuel_adjust?.includes('Dead Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Dead Fuel Catalog (Fuel Model Z) is Required')
    }),

    live_catalog_id_v: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('V') && fuel_adjust?.includes('Live Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Live Fuel Catalog (Fuel Model V) is Required')
    }),
    live_catalog_id_w: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('W') && fuel_adjust?.includes('Live Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Live Fuel Catalog (Fuel Model W) is Required')
    }),
    live_catalog_id_x: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('X') && fuel_adjust?.includes('Live Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Live Fuel Catalog (Fuel Model X) is Required')
    }),
    live_catalog_id_y: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('Y') && fuel_adjust?.includes('Live Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Live Fuel Catalog (Fuel Model Y) is Required')
    }),
    live_catalog_id_z: Yup.string().nullable()
      .when(['fuel_model', 'fuel_adjust'], {
        is: (fuel_model, fuel_adjust) => fuel_model?.includes('Z') && fuel_adjust?.includes('Live Fuel Catalog'),
        then: Yup.string().nullable()
          .required('Live Fuel Catalog (Fuel Model Z) is Required')
    }),

  }
);

interface FormVals {
  fuelCatalogId_v: number | null,
  fuelCatalogId_w: number | null,
  fuelCatalogId_x: number | null,
  fuelCatalogId_y: number | null,
  fuelCatalogId_z: number | null,

  fuelCatalogName: string,

  fuel_model: string | null,
  mxd_override_v: number | null,
  mxd_override_w: number | null,
  mxd_override_x: number | null,
  mxd_override_y: number | null,
  mxd_override_z: number | null,


  scm_v: number | null,
  scm_w: number | null,
  scm_x: number | null,
  scm_y: number | null,
  scm_z: number | null,


  slope_class_v: number | null,
  slope_class_w: number | null,
  slope_class_x: number | null,
  slope_class_y: number | null,
  slope_class_z: number | null,

  grass_type_v: string | null,
  grass_type_w: string | null,
  grass_type_x: string | null,
  grass_type_y: string | null,
  grass_type_z: string | null,

  fuel_adjust: any |null,
  drought_id: string | null,
  dead_id: string | null,
  live_catalog_id: string | null,

  drought_id_v: string | null,
  drought_id_w: string | null,
  drought_id_x: string | null,
  drought_id_y: string | null,
  drought_id_z: string | null,

  dead_id_v: string | null,
  dead_id_w: string | null,
  dead_id_x: string | null,
  dead_id_y: string | null,
  dead_id_z: string | null,

  live_catalog_id_v: string | null,
  live_catalog_id_w: string | null,
  live_catalog_id_x: string | null,
  live_catalog_id_y: string | null,
  live_catalog_id_z: string | null,

}
export const AddFuelCatalog: React.FC = () => {

  const history = useHistory();
  const location = useLocation();
  
  const [fuelModels, setFuelModels] = useState<any[]>(['All', 'V', 'W', 'X', 'Y', 'Z'])
  const [fuelIndividualChange, setFuelIndividualChange] = useState<string[]>([])

  const [, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);

  const [openDroughtModal, setOpenDroughtModal] = useState<boolean>(false);
  const [createUpdateDroughtSuccess, setCreateUpdateDroughtSuccess] = useState<boolean>(true);

  const [droughtOptions, setDroughtOptions] = useState<any[]>([])
  const [openDeadModal, setOpenDeadModal] = useState<boolean>(false);
  const [createUpdateDeadSuccess, setCreateUpdateDeadSuccess] = useState<boolean>(true);
  const [deadOptions, setDeadOptions] = useState<any[]>([])

  const [openLiveModal, setOpenLiveModal] = useState<boolean>(false);
  const [createUpdateLiveSuccess, setCreateUpdateLiveSuccess] = useState<boolean>(true);
  const [liveOptions, setLiveOptions] = useState<any[]>([]);

  const [editFuel, setEditFuel] = useState<any>(null)
  const [loading,setLoading] = useState<boolean>(false);

  const [originalEditFuelId, setOriginalEditFuelId] = useState<string|null>(null)

    /**
     * Make the api call for live catalogs
     */
  const liveFuelCatalogCallback = useCallback((request: any) => {
    (async () => {
      try {
        const liveFuelCatalogResults: any = await getLiveCatalogCall(request, addAlert);
        if (liveFuelCatalogResults.data.length > 0) {
          setLiveOptions(liveFuelCatalogResults.data)
        } else {
          setLiveOptions([])
        }
        setCreateUpdateLiveSuccess(false)

      } catch (error) {
        setLiveOptions([])
        setCreateUpdateLiveSuccess(false)

        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);

  /** Make the api call for the drought catalogs */
  const droughtCatalogCallback = useCallback((request: any) => {
    (async () => {
      try {
        const droughtCatalogResponse: any = await getDroughtCatalogCall(request, addAlert);
        if (droughtCatalogResponse.data.length > 0) {
          setDroughtOptions(droughtCatalogResponse.data)

        } else {
          setDroughtOptions([])          
        }
        setCreateUpdateDroughtSuccess(false)

      } catch (error) {
        setDroughtOptions([])
        setCreateUpdateDroughtSuccess(false)

        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);

  /** Make the api call for the dead catalogs */
  const deadCatalogCallback = useCallback((request: any) => {
    (async () => {
      try {
        const deadCatalogResponse: any = await getDeadCatalogsCall(request, addAlert);
        if (deadCatalogResponse.data.length > 0) {
          setDeadOptions(deadCatalogResponse.data)

        } else {
          setDeadOptions([])
        }
        setCreateUpdateDeadSuccess(false)

      } catch (error) {
        setCreateUpdateDeadSuccess(false)
        setDeadOptions([])
        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);

  const handleCloseDroughtModal = (_event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      setOpenDroughtModal(false);
    }
    setOpenDroughtModal(false);
  }

  const handleCloseDeadModal = (_event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      setOpenDeadModal(false);
    }
    setOpenDeadModal(false);
  }

  const handleCloseLiveModal = (_event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      setOpenLiveModal(false);
    }
    setOpenLiveModal(false);
  }

  const cancelClick = () => {
    if (editFuel) {
      const stateOfLocation: any = location?.state
      if (stateOfLocation?.tab === 1) {
        history.push(
          {
            pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/stationAssign/${originalEditFuelId}`
          }
        )
      }
      else if (stateOfLocation?.tab === 2) {
        history.push(
          {
            pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/audit/${originalEditFuelId}`
          }
        )
      }
      else {
        history.push(
          {
            pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/detail/${originalEditFuelId}`
          }
        )
      }
    } else {
      history.push(
        {
          pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}`
        }
      )
    }
  }

  const [initVals, setInitVals] = useState<FormVals>({
    fuelCatalogId_v: null,
    fuelCatalogId_w: null,
    fuelCatalogId_x: null,
    fuelCatalogId_y: null,
    fuelCatalogId_z: null,

    fuelCatalogName: '',

    fuel_model: "V,W,X,Y,Z",
    mxd_override_v: 15,
    mxd_override_w: 15,
    mxd_override_x: 25,
    mxd_override_y: 25,
    mxd_override_z: 25,

    scm_v: 108,
    scm_w: 62,
    scm_x: 104,
    scm_y: 5,
    scm_z: 19,


    slope_class_v: 1,
    slope_class_w: 1,
    slope_class_x: 1,
    slope_class_y: 1,
    slope_class_z: 1,

    grass_type_v: null,
    grass_type_w: null,
    grass_type_x: null,
    grass_type_y: null,
    grass_type_z: null,

    fuel_adjust: null,
    drought_id: null,
    dead_id:  null,
    live_catalog_id: null,

    drought_id_v: null,
    drought_id_w: null,
    drought_id_x: null,
    drought_id_y: null,
    drought_id_z: null,

    dead_id_v: null,
    dead_id_w: null,
    dead_id_x: null,
    dead_id_y: null,
    dead_id_z: null,

    live_catalog_id_v: null,
    live_catalog_id_w: null,
    live_catalog_id_x: null,
    live_catalog_id_y: null,
    live_catalog_id_z: null,
  })

    /** Make the api call for the fuel catalog edit */
  const fuelCatalogCallback = useCallback((request: any, idClicked:Number) => {
    (async () => {
      try {
        let initValueOfFuel = {...initVals}
        const fuelForThisCatalogName: any = await getFuelCatalogs(request, addAlert);
        if (fuelForThisCatalogName.data.length > 0) {
          fuelForThisCatalogName.data?.forEach((fmCatalog:any)=>{
            const lowerCaseFM = (fmCatalog.fuelModel).toString().toLowerCase()

            if(idClicked === fmCatalog.catalogId){
              setFuelModels([fmCatalog.fuelModel.toString()])
              initValueOfFuel.fuel_model = fmCatalog.fuelModel;
              initValueOfFuel.fuelCatalogName = fmCatalog.catalogName;
              initValueOfFuel.drought_id = fmCatalog.droughtCatalog.id;
              initValueOfFuel.dead_id = fmCatalog.deadFuelCatalog.id;
              initValueOfFuel.live_catalog_id = fmCatalog.liveFuelCatalog.id;

            }
            initValueOfFuel[`fuelCatalogId_${lowerCaseFM}`] = fmCatalog.catalogId;
            initValueOfFuel[`mxd_override_${lowerCaseFM}`] = fmCatalog.mxdOverride;
            initValueOfFuel[`scm_${lowerCaseFM}`] = fmCatalog.scm;
            initValueOfFuel[`slope_class_${lowerCaseFM}`] = fmCatalog.slopeClass;
            initValueOfFuel[`grass_type_${lowerCaseFM}`] = fmCatalog.grassType;
            initValueOfFuel[`dead_id_${lowerCaseFM}`] = fmCatalog.deadFuelCatalog.id;
            initValueOfFuel[`drought_id_${lowerCaseFM}`] = fmCatalog.droughtCatalog.id;
            initValueOfFuel[`live_catalog_id_${lowerCaseFM}`] = fmCatalog.liveFuelCatalog.id;
          });
          setInitVals(initValueOfFuel)
          setEditFuel(initValueOfFuel)
        }

      } catch (error) {
        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert, initVals]);


  const handleSubmit = (values: any) => {
    const stringArray = decodeURIComponent(location.pathname).split("/");
    const filterOutAll = fuelModels?.filter(fm => fm !== 'All');
    const fuelModelUpdatesToSend = filterOutAll.map((fm:any)=>{
      const lowerCaseFM = fm.toString().toLowerCase();
        return {
          id: stringArray?.length>3?Number(values[`fuelCatalogId_${lowerCaseFM}`]):null,
          catalogName: values.fuelCatalogName,
          fuelModel: fm?.toString(),
          slopeClass: Number(values[`slope_class_${lowerCaseFM}`]),
          grassType: values[`grass_type_${lowerCaseFM}`]?.toString(),
          mxdOverride: Number(values[`mxd_override_${lowerCaseFM}`]),
          scm: Number(values[`scm_${lowerCaseFM}`]),
          droughtCatalogId: Number(values[`drought_id_${lowerCaseFM}`]),
          liveFuelCatalogId: Number(values[`live_catalog_id_${lowerCaseFM}`]),
          deadFuelCatalogId: Number(values[`dead_id_${lowerCaseFM}`]),
        }
    });
    (async () => {
      setLoading(true)
      try {
        const response: any = await createUpdateFuelModelCatalogCall(fuelModelUpdatesToSend, addAlert);
        if (response?.data.data.createUpdateFireDangerModelCatalog.message?.length > 0) {
          //save banner
          if (values.fuelCatalogId_x === null && values.fuelCatalogId_w === null
            && values.fuelCatalogId_x === null && values.fuelCatalogId_y === null
            && values.fuelCatalogId_z === null
          ) {
            
            errorResponse('', addAlert, `The Fuel Model Catalog(s), ${response.data.data.createUpdateFireDangerModelCatalog.data[0][0].catalog_name} for ${values.fuel_model}, was saved.`, true);
          }
          else {
            errorResponse('', addAlert, `The changes to the Fuel Model Catalog(s),  ${response.data.data.createUpdateFireDangerModelCatalog.data[0][0].catalog_name} for ${values.fuel_model}, was saved.`, true);
          }

        }
        cancelClick();
        setLoading(false)
      }
      catch (error: any) {
        setLoading(false)
        console.log('error', error);
        if(error?.message?.toLowerCase().includes('unique')){
          addAlert({
            id: '',
            message: alertMessages.uniqueCatalogName,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          })
        }
        else{
          addAlert({
            id: '',
            message: alertMessages.fuelModelCatalogCreate,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          })
        }
      }
    })();
  }

  /*below is to set default values*/
  useEffect(() => {
    const stringArray = decodeURIComponent(location.pathname).split("/");
    if( stringArray?.length>3 && !editFuel){
      const catalogName = stringArray[stringArray?.length-1];
      const idClicked = stringArray[stringArray?.length-2];
      setOriginalEditFuelId(idClicked)
      const request = {
        id: null,
        catalogName: catalogName,
        perPage: 10000,
        page: 0,
        sortBy: 'fire_danger_model_name',
        sortOrder: 'asc',
        fuelModel: 'ALL'
      }
      fuelCatalogCallback(request, Number(idClicked))
    }
    if(!editFuel){

      let initValues = {
        fuelCatalogId_v: null,
        fuelCatalogId_w: null,
        fuelCatalogId_x: null,
        fuelCatalogId_y: null,
        fuelCatalogId_z: null,
        fuelCatalogName: '',

        fuel_model: "V,W,X,Y,Z",
        mxd_override_v: 15,
        mxd_override_w: 15,
        mxd_override_x: 25,
        mxd_override_y: 25,
        mxd_override_z: 25,

        scm_v: 108,
        scm_w: 62,
        scm_x: 104,
        scm_y: 5,
        scm_z: 19,


        slope_class_v: 1,
        slope_class_w: 1,
        slope_class_x: 1,
        slope_class_y: 1,
        slope_class_z: 1,

        grass_type_v: 'P',
        grass_type_w: 'P',
        grass_type_x: 'P',
        grass_type_y: 'P',
        grass_type_z: 'P',
        
        fuel_adjust: null,
        drought_id: null,
        dead_id:  null,
        live_catalog_id: null,

        drought_id_v: null,
        drought_id_w: null,
        drought_id_x: null,
        drought_id_y: null,
        drought_id_z: null,

        dead_id_v: null,
        dead_id_w: null,
        dead_id_x: null,
        dead_id_y: null,
        dead_id_z: null,

        live_catalog_id_v: null,
        live_catalog_id_w: null,
        live_catalog_id_x: null,
        live_catalog_id_y: null,
        live_catalog_id_z: null,
      }
      setInitVals(initValues);
    }
  }, [addAlert, location.pathname,fuelCatalogCallback, editFuel, setOriginalEditFuelId]);


  useEffect(()=>{
    if (createUpdateDeadSuccess) {

      const request = {
        id: null,
        perPage: 10000,
        page: 0,
        sortBy: 'dead_fuel_parameter_catalog_name',
        sortOrder: 'asc',
      }
      deadCatalogCallback(request);
    }

  },[createUpdateDeadSuccess, deadCatalogCallback,])


  useEffect(()=>{
    if (createUpdateDroughtSuccess) {
      const request = {
        id: null,
        perPage: 10000,
        page: 0,
        sortBy: 'kbdi_parameter_catalog_name',
        sortOrder: 'asc',
      }
      droughtCatalogCallback(request);
    }
  },[createUpdateDroughtSuccess,droughtCatalogCallback,]);


  useEffect(()=>{
    if (createUpdateLiveSuccess) {
      const request = {
        id: null,
        perPage: 10000,
        page: 0,
        sortBy: 'catalog_name',
        sortOrder: 'ASC'
      }
      liveFuelCatalogCallback(request);
    }
  },[createUpdateLiveSuccess,liveFuelCatalogCallback]);


  return (
    <GridContainer className="removePadding " style={{ height: '90%', maxHeight: '100%', minHeight: '50%', overflowX: 'hidden', paddingBottom: '.2rem', paddingTop: '1rem!important',width:'100vw' }}>
      {(loading) && (

        <div>
          <LoadingSpinner />
        </div>
      )}
      <Grid row gap="md" className=''>
        <Grid
          desktop={{ col: 12 }}
          className="display-flex flex-row flex-align-center flex-justify margin-bottom-205" style={{paddingRight:'2rem', paddingLeft: '2rem', paddingTop:'2rem'}}>
          {
            editFuel ? (
              <span className="font-sans-lg text-bold">Edit Fuel Model Catalog</span>
            ):(
            <span className="font-sans-lg text-bold">Add a New Fuel Model Catalog</span>
            )
          }
        </Grid>
      </Grid>

      <Formik
        initialValues={initVals}
        validateOnBlur={true}
        validateOnChange={true}
        validateOnMount={true}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => handleSubmit(values)}>
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          isValid,
          setFieldTouched,
          handleBlur
        }) => {
          const setValue = (event: any, type: string) => {
            setFieldTouched(type);
            if (event.target.value.length === 1 && /^\s/.test(event.target.value)) {
              return
            }
            setFieldValue(type, event.target.value);
            
          }

          const deleteFuelModels = (value: any) => {
            setFieldTouched('fuel_model');
            const removeSelection = fuelModels?.filter((fm) => fm !== value && fm !== 'All')
            setFuelModels(removeSelection)
            setFieldValue('fuel_model', removeSelection.join(','))
          }

          const handleFuelModels = (event: SelectChangeEvent) => {
            setFieldTouched('fuel_model');
            const val: any = event.target.value;
            let fmodels = fuelModels;

            let modelsAccordingToSelect = fuelIndividualChange;
            const addModels = fuelIndividualChange;
            if(editFuel){
              if(val.length>=1){
                if(!addModels.includes('Drought Catalog')){
                  modelsAccordingToSelect.push('Drought Catalog')
                }
                if(!addModels.includes('Dead Fuel Catalog')){
                  modelsAccordingToSelect.push('Dead Fuel Catalog')
                }
                if(!addModels.includes('Live Fuel Catalog')){
                  modelsAccordingToSelect.push('Live Fuel Catalog')
                }
                setFuelIndividualChange(modelsAccordingToSelect)
                setFieldValue('fuel_adjust', modelsAccordingToSelect.join(','))
              }
            }
            if (val.includes('All')) {
              if (fmodels.includes('All')) {
                const filterOutAll = val.filter(fm => fm !== 'All');
                setFuelModels(filterOutAll);
                setFieldValue('fuel_model', filterOutAll.join(','))

              }
              else {
                const filtered = FuelModels.filter(fm => fm !== 'All')
                setFuelModels(FuelModels);
                setFieldValue('fuel_model', filtered.join(','))
              }

            }
            else {
              if (fmodels.includes('All')) {
                setFuelModels([])
                setFieldValue('fuel_model', '')
              } else {
                setFuelModels(val)
                setFieldValue('fuel_model', val.join(','))
              }
            }
          };

          const deleteAdjustment = (value: any) => {
            let removeSelection = fuelIndividualChange.filter((fm) => fm !== value)
            setFuelIndividualChange(removeSelection)
            setFieldValue('fuel_adjust', removeSelection?.join(','))
          };
          const addAdjustment = (e: SelectChangeEvent)=>{
            const val: any = e.target.value;
            let addModels = fuelIndividualChange;
            if (val.includes('All')) {
              if (addModels.includes('All')) {
                const filterOutAll = val.filter((fm:string) => fm !== 'All');
                setFuelIndividualChange(filterOutAll);
                setFieldValue('fuel_adjust',filterOutAll?.join(','))

              }
              else {
                const filtered = FuelCatalogBasedOnModels.filter(fm => fm !== 'All')
                setFuelIndividualChange(FuelCatalogBasedOnModels);
                setFieldValue('fuel_adjust', filtered.join(','))
              }

            }
            else {
              if (addModels.includes('All')) {
                setFuelIndividualChange([])
                setFieldValue('fuel_adjust', '')
              } else {
                setFuelIndividualChange(val)
                setFieldValue('fuel_adjust', val.join(','))
              }
            }
          };

          const handleCatalogSelection = (value:any, fieldType: string, id: string, singleFuelModel:boolean) =>{
            setFieldValue(fieldType, value[`${id}`])
            !singleFuelModel && fuelModels.forEach((fm:any)=>{
              fm!=='All' &&
              setFieldValue(`${fieldType}_${fm.toLowerCase()}`, value[`${id}`])
            })
          }

          const setIntValue = (event: any, type: string) => {
            setFieldTouched(type);
            const inputValue = event.target.value;
            const filteredValue = inputValue.replaceAll(/[^0-9]/g, '');
            const intValue = filteredValue === '' ? '' : Number.parseInt(filteredValue, 10);
            setFieldValue(type, intValue);
          }   
          const setCharacterValue = (event: any, type: string) => {
            setFieldTouched(type);
            const inputValue = event.target.value;
            const filteredValue = inputValue.replaceAll(/[^a-zA-Z]/g, '');
            setFieldValue(type, filteredValue.toUpperCase());
          }  

          return (
            <Form onSubmit={handleSubmit} >
                <Modal //Modal to open edit/create drought
                  className="smooth-scrollbar"
                  open={openDroughtModal}
                  onClose={handleCloseDroughtModal}
                >
                  <div>
                    <IconButton aria-label='close' style={{ left: '73.5%', top: '5.5%', position: 'absolute', cursor: 'pointer' }} onClick={() => { setOpenDroughtModal(false) }}>
                      <CloseIcon style={{ color: 'white' }} />
                    </IconButton>
                    <Box sx={modalStyle} className="smooth-scrollbar removePadding" >
                      {
                        <AddDroughtCatalog setOpen={setOpenDroughtModal} setCreateUpdateDroughtSuccess={setCreateUpdateDroughtSuccess}/>
                      }
                    </Box>
                  </div>
                </Modal>
                <Modal //Modal to open edit/create dead
                  className="smooth-scrollbar"
                  open={openDeadModal}
                  onClose={handleCloseDeadModal}
                >
                  <div>
                    <IconButton aria-label='close' style={{ left: '73.5%', top: '5.5%', position: 'absolute', cursor: 'pointer' }} onClick={() => { setOpenDeadModal(false) }}>
                      <CloseIcon style={{ color: 'white' }} />
                    </IconButton>
                    <Box sx={modalStyle} className="smooth-scrollbar removePadding" >
                      {
                        <AddDeadCatalog setOpen={setOpenDeadModal} setCreateUpdateDeadSuccess={setCreateUpdateDeadSuccess} />
                      }
                    </Box>
                  </div>
                </Modal>
                <Modal //Modal to open edit/create live fuel
                  className="smooth-scrollbar"
                  open={openLiveModal}
                  onClose={handleCloseLiveModal}
                >
                  <div>
                    <IconButton aria-label='close' style={{ left: '73.5%', top: '5.5%', position: 'absolute', cursor: 'pointer' }} onClick={() => { setOpenLiveModal(false) }}>
                      <CloseIcon style={{ color: 'white' }} />
                    </IconButton>
                    <Box sx={modalStyle} className="smooth-scrollbar removePadding" >
                      {
                        <AddLiveCatalog setOpen={setOpenLiveModal} setCreateUpdateLiveSuccess={setCreateUpdateLiveSuccess} />
                      }
                    </Box>
                  </div>
                </Modal>

                <Grid row gap='md' desktop={{ col: 12 }} style={{paddingLeft: '2rem', paddingRight: '2rem'}}>
                  <Grid desktop={{col:7}}>
                  <label id={'CreateUpdatefuelCatalogName'} htmlFor={'CreateUpdatefuelCatalogName'}>Fuel Model Catalog Name*</label>
                  <TextField
                    value={values.fuelCatalogName}
                    onBlur={() => setFieldTouched('fuelCatalogName')}
                    error={!!errors.fuelCatalogName && !!touched.fuelCatalogName}
                    helperText={errors.fuelCatalogName && touched.fuelCatalogName ? errors.fuelCatalogName : ''}
                    id={'CreateUpdatefuelCatalogName'}
                    type={'string'}
                    size='medium'
                    fullWidth
                    onChange={(e: any) => { setValue(e, 'fuelCatalogName') }}
                    placeholder={'Enter Fuel Model Catalog Name'}
                  />
                  </Grid>
                  <Grid desktop={{col:3}}/>
                  <Grid desktop={{ col: 1 }}>
                    <Button variant="outlined" style={{ backgroundColor: 'white' }} onClick={() => cancelClick()}>
                      Cancel
                    </Button>
                  </Grid>
                  {
                  editFuel ? (
                    <Grid desktop={{ col: 1 }} className='modalSubmitDiv'>
                      <Button variant="contained" type="submit" aria-disabled={!isValid}>
                        Save
                      </Button>
                    </Grid>
                  ) : (
                    <Grid desktop={{ col: 1 }} className='modalSubmitDiv'>
                      <Button variant="contained" type="submit" aria-disabled={!isValid}>
                        Add
                      </Button>
                    </Grid>
                    )
                  }
                </Grid>
                <Grid row desktop={{ col: 6 }} style={{paddingLeft: '2rem', paddingRight: '2rem'}}>
                  <label id='fuelModels'>Fuel Model(s)*
                    <Select
                      multiple
                      value={fuelModels}
                      id='fuelModels'
                      input={<OutlinedInput label="Tag" />}
                      onChange={(e: any) => { handleFuelModels(e) }}
                      MenuProps={MenuProps}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            value !== 'All' &&
                            <Chip key={value}
                              label={value}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onKeyDown={(event) => { event.key === "Backspace" && deleteFuelModels(value) }}
                              onDelete={() => { deleteFuelModels(value) }}
                              deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key === 'Enter' && deleteFuelModels(value) }} />}
                            />
                          ))}
                        </Box>
                      )}
                      error={!!errors.fuel_model && !!touched.fuel_model}

                    >
                      {FuelModels.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </label>
                  <span style={{ color: '#d32f2f' }}>{touched.fuel_model && errors.fuel_model}</span>
                </Grid>
                <Grid row desktop={{ col: 12 }} style={{paddingLeft: '2rem', paddingRight: '2rem'}}>
                  <label id='fuelMulti'>Adjust for Each Fuel
                    <Select
                      multiple
                      value={fuelIndividualChange}
                      id='fuelMulti'
                      input={<OutlinedInput label="Tag" />}
                      onChange={(e: any) => { addAdjustment(e) }}
                      MenuProps={MenuProps}
                      placeholder='None'
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            value !== 'All' &&
                            <Chip key={value}
                              label={value}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onKeyDown={(event) => { event.key === "Backspace" && deleteAdjustment(value) }}
                              onDelete={() => { deleteAdjustment(value) }}
                              deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key === 'Enter' && deleteAdjustment(value) }} />}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {FuelCatalogBasedOnModels.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </label>
                  <span style={{ color: '#d32f2f' }}>{touched.fuel_model && errors.fuel_model}</span>
                </Grid>

                <Divider style={{marginTop: '1rem'}}/>
                
                
                <GridContainer style={{maxHeight: '30vh', overflowY:'auto'}}>

              
{/************************* SLOPE CLASSES */}               
                {
                  /******  FUEL MODEL V Slope Class */
                  fuelModels.includes('V') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSlopeClass-V`} htmlFor={`createUpdateSlopeClass-V`}>Slope Class (Fuel Model V)*</label>
                    <TextField
                      value={values.slope_class_v}
                      onBlur={() => setFieldTouched('slope_class_v')}
                      error={!!errors.slope_class_v && !!touched.slope_class_v}
                      helperText={errors.slope_class_v && touched.slope_class_v ? errors.slope_class_v : ''}
                      id={`createUpdateSlopeClass-V`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'slope_class_v') }}
                      placeholder={'Enter Slope Class (Fuel Model V)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('Slope Class')}
                      
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 5
                        }
                      }}
                    />
                    
                  </Grid>
                }
                
                {
                  /******  FUEL MODEL W SLOPE CLASS*/
                  fuelModels.includes('W') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSlopeClass-W`} htmlFor={`createUpdateSlopeClass-W`}>Slope Class (Fuel Model W)*</label>
                    <TextField
                      value={values.slope_class_w}
                      onBlur={() => setFieldTouched('slope_class_w')}
                      error={!!errors.slope_class_w && !!touched.slope_class_w}
                      helperText={errors.slope_class_w && touched.slope_class_w ? errors.slope_class_w : ''}
                      id={`createUpdateSlopeClass-W`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'slope_class_w') }}
                      placeholder={'Enter Slope Class (Fuel Model W)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('Slope Class')}
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 5
                        }
                      }}
                    />
                    
                  </Grid>
                }

                {
                  /****** FUEL MODEL X Slope Class*/
                  fuelModels.includes('X') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSlopeClass-X`} htmlFor={`createUpdateSlopeClass-X`}>Slope Class (Fuel Model X)*</label>
                    <TextField
                      value={values.slope_class_x}
                      onBlur={() => setFieldTouched('slope_class_x')}
                      error={!!errors.slope_class_x && !!touched.slope_class_x}
                      helperText={errors.slope_class_x && touched.slope_class_x ? errors.slope_class_x : ''}
                      id={`createUpdateSlopeClass-X`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'slope_class_x') }}
                      placeholder={'Enter Slope Class (Fuel Model X)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('Slope Class')}
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 5
                        }
                      }}
                    />
                    
                  </Grid>
                }
                {
                  /****** FUEL MODEL Y Slope Class*/
                  fuelModels.includes('Y') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSlopeClass-Y`} htmlFor={`createUpdateSlopeClass-Y`}>Slope Class (Fuel Model Y)*</label>
                    <TextField
                      value={values.slope_class_y}
                      onBlur={() => setFieldTouched('slope_class_y')}
                      error={!!errors.slope_class_y && !!touched.slope_class_y}
                      helperText={errors.slope_class_y && touched.slope_class_y ? errors.slope_class_y : ''}
                      id={`createUpdateSlopeClass-Y`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'slope_class_y') }}
                      placeholder={'Enter Slope Class (Fuel Model Y)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('Slope Class')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 5
                        }
                      }}
                    />
                    
                  </Grid>
                }
                {
                  /****** FUEL MODEL Z Slope Class*/
                  fuelModels.includes('Z') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSlopeClass-Z`} htmlFor={`createUpdateSlopeClass-Z`}>Slope Class (Fuel Model Z)*</label>
                    <TextField
                      value={values.slope_class_z}
                      onBlur={() => setFieldTouched('slope_class_z')}
                      error={!!errors.slope_class_z && !!touched.slope_class_z}
                      helperText={errors.slope_class_z && touched.slope_class_z ? errors.slope_class_z : ''}
                      id={`createUpdateSlopeClass-Z`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'slope_class_z') }}
                      placeholder={'Enter Slope Class (Fuel Model Z)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('Slope Class')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 5
                        }
                      }}
                    />
                    
                  </Grid>
                }
                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
                
{/************************* GRASS TYPES */}               
                {
                  /******  FUEL MODEL V Grass Type */
                  fuelModels.includes('V') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateGrassType-V`} htmlFor={`createUpdateGrassType-V`}>Grass Type (Fuel Model V)*</label>
                    <Select
                      value={values.grass_type_v}
                      onBlur={() => setFieldTouched('grass_type_v')}
                      id={`createUpdateGrassType-V`}
                      input={<OutlinedInput label="Tag"/>}
                      onChange={(e: any) => { setCharacterValue(e, 'grass_type_v') }}
                      MenuProps={MenuProps}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onInput={(e) => validateCharactersLength(e, 1)}
                      disabled={!fuelIndividualChange.includes('Grass Type')}
                      error={!!errors.grass_type_v && !!touched.grass_type_v}
                    >
                      {GrassTypeOptions.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                }
                
                {
                  /******  FUEL MODEL W GRASS TYPE*/
                  fuelModels.includes('W') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateGrassType-W`} htmlFor={`createUpdateGrassType-W`}>Grass Type (Fuel Model W)*</label>
                    <Select
                      value={values.grass_type_w}
                      onBlur={() => setFieldTouched('grass_type_w')}
                      id={`createUpdateGrassType-W`}
                      input={<OutlinedInput label="Tag"/>}
                      onChange={(e: any) => { setCharacterValue(e, 'grass_type_w') }}
                      MenuProps={MenuProps}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onInput={(e) => validateCharactersLength(e, 1)}
                      disabled={!fuelIndividualChange.includes('Grass Type')}
                      error={!!errors.grass_type_w && !!touched.grass_type_w}
                    >
                      {GrassTypeOptions.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                }

                {
                  /****** FUEL MODEL X Grass Type*/
                  fuelModels.includes('X') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateGrassType-X`} htmlFor={`createUpdateGrassType-X`}>Grass Type (Fuel Model X)*</label>
                    <Select
                      value={values.grass_type_x}
                      onBlur={() => setFieldTouched('grass_type_x')}
                      id={`createUpdateGrassType-X`}
                      input={<OutlinedInput label="Tag"/>}
                      onChange={(e: any) => { setCharacterValue(e, 'grass_type_x') }}
                      MenuProps={MenuProps}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onInput={(e) => validateCharactersLength(e, 1)}
                      disabled={!fuelIndividualChange.includes('Grass Type')}
                      error={!!errors.grass_type_x && !!touched.grass_type_x}
                    >
                      {GrassTypeOptions.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                }
                {
                  /****** FUEL MODEL Y Grass Type*/
                  fuelModels.includes('Y') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateGrassType-Y`} htmlFor={`createUpdateGrassType-Y`}>Grass Type (Fuel Model Y)*</label>
                    <Select
                      value={values.grass_type_y}
                      onBlur={() => setFieldTouched('grass_type_y')}
                      id={`createUpdateGrassType-Y`}
                      input={<OutlinedInput label="Tag"/>}
                      onChange={(e: any) => { setCharacterValue(e, 'grass_type_y') }}
                      MenuProps={MenuProps}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onInput={(e) => validateCharactersLength(e, 1)}
                      disabled={!fuelIndividualChange.includes('Grass Type')}
                      error={!!errors.grass_type_y && !!touched.grass_type_y}
                    >
                      {GrassTypeOptions.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                }
                {
                  /****** FUEL MODEL Z Grass Type*/
                  fuelModels.includes('Z') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateGrassType-Z`} htmlFor={`createUpdateGrassType-Z`}>Grass Type (Fuel Model Z)*</label>
                    <Select
                      value={values.grass_type_z}
                      onBlur={() => setFieldTouched('grass_type_z')}
                      id={`createUpdateGrassType-Z`}
                      input={<OutlinedInput label="Tag"/>}
                      onChange={(e: any) => { setCharacterValue(e, 'grass_type_z') }}
                      MenuProps={MenuProps}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onInput={(e) => validateCharactersLength(e, 1)}
                      disabled={!fuelIndividualChange.includes('Grass Type')}
                      error={!!errors.grass_type_z && !!touched.grass_type_z}
                    >
                      {GrassTypeOptions.map((fm) => (
                        <MenuItem
                          key={fm}
                          value={fm}
                        >
                          {fm}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                }
                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
{/************************* MXD OVERRIDE */}               

                {
                  /******  FUEL MODEL V MXD*/
                  fuelModels.includes('V') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateMXDOverride-V`} htmlFor={`createUpdateMXDOverride-V`}>MXD Override (Fuel Model V)*</label>
                    <TextField
                      value={values.mxd_override_v}
                      onBlur={() => setFieldTouched('mxd_override_v')}
                      error={!!errors.mxd_override_v && !!touched.mxd_override_v}
                      helperText={errors.mxd_override_v && touched.mxd_override_v ? errors.mxd_override_v : ''}
                      id={`createUpdateMXDOverride-V`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'mxd_override_v') }}
                      placeholder={'Enter MXD Override (Fuel Model V)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('MXD Override')}
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 100
                        }
                      }}
                    />
                    
                  </Grid>
                }
                
                {
                  /******  FUEL MODEL W MXD*/
                  fuelModels.includes('W') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateMXDOverride-W`} htmlFor={`createUpdateMXDOverride-W`}>MXD Override (Fuel Model W)*</label>
                    <TextField
                      value={values.mxd_override_w}
                      onBlur={() => setFieldTouched('mxd_override_w')}
                      error={!!errors.mxd_override_w && !!touched.mxd_override_w}
                      helperText={errors.mxd_override_w && touched.mxd_override_w ? errors.mxd_override_w : ''}
                      id={`createUpdateMXDOverride-W`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'mxd_override_w') }}
                      placeholder={'Enter MXD Override (Fuel Model W)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('MXD Override')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 100
                        }
                      }}
                    />
                    
                  </Grid>
                }

                {
                  /****** FUEL MODEL X MXD*/
                  fuelModels.includes('X') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateMXDOverride-X`} htmlFor={`createUpdateMXDOverride-X`}>MXD Override (Fuel Model X)*</label>
                    <TextField
                      value={values.mxd_override_x}
                      onBlur={() => setFieldTouched('mxd_override_x')}
                      error={!!errors.mxd_override_x && !!touched.mxd_override_x}
                      helperText={errors.mxd_override_x && touched.mxd_override_x ? errors.mxd_override_x : ''}
                      id={`createUpdateMXDOverride-X`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'mxd_override_x') }}
                      placeholder={'Enter MXD Override (Fuel Model X)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('MXD Override')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 100
                        }
                      }}
                    />
                    
                  </Grid>
                }
                {
                   /****** FUEL MODEL X MXD*/
                  fuelModels.includes('Y') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateMXDOverride-Y`} htmlFor={`createUpdateMXDOverride-Y`}>MXD Override (Fuel Model Y)*</label>
                    <TextField
                      value={values.mxd_override_y}
                      onBlur={() => setFieldTouched('mxd_override_y')}
                      error={!!errors.mxd_override_y && !!touched.mxd_override_y}
                      helperText={errors.mxd_override_y && touched.mxd_override_y ? errors.mxd_override_y : ''}
                      id={`createUpdateMXDOverride-Y`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'mxd_override_y') }}
                      placeholder={'Enter MXD Override (Fuel Model Y)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('MXD Override')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 100
                        }
                      }}
                    />
                    
                  </Grid>
                }
                {
                   /****** FUEL MODEL Z MXD*/
                  fuelModels.includes('Z') && 
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateMXDOverride-Z`} htmlFor={`createUpdateMXDOverride-Z`}>MXD Override (Fuel Model Z)*</label>
                    <TextField
                      value={values.mxd_override_z}
                      onBlur={() => setFieldTouched('mxd_override_z')}
                      error={!!errors.mxd_override_z && !!touched.mxd_override_z}
                      helperText={errors.mxd_override_z && touched.mxd_override_z ? errors.mxd_override_z : ''}
                      id={`createUpdateMXDOverride-Z`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'mxd_override_z') }}
                      placeholder={'Enter MXD Override (Fuel Model Z)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('MXD Override')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 100
                        }
                      }}
                    />
                    
                  </Grid>
                }
                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
{/************************* SCM */}               

                {
                  /******  FUEL MODEL V SCM */
                  fuelModels.includes('V') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSCM-V`} htmlFor={`createUpdateSCM-V`}>SCM (Fuel Model V)*</label>
                    <TextField
                      value={values.scm_v}
                      onBlur={() => setFieldTouched('scm_v')}
                      error={!!errors.scm_v && !!touched.scm_v}
                      helperText={errors.scm_v && touched.scm_v ? errors.scm_v : ''}
                      id={`createUpdateSCM-V`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'scm_v') }}
                      placeholder={'Enter SCM (Fuel Model V)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('SCM')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 1000
                        }
                      }}
                    />
                    
                  </Grid>
                }
                
                {
                  /******  FUEL MODEL W SCM*/
                  fuelModels.includes('W') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSCM-W`} htmlFor={`createUpdateSCM-W`}>SCM (Fuel Model W)*</label>
                    <TextField
                      value={values.scm_w}
                      onBlur={() => setFieldTouched('scm_w')}
                      error={!!errors.scm_w && !!touched.scm_w}
                      helperText={errors.scm_w && touched.scm_w ? errors.scm_w : ''}
                      id={`createUpdateSCM-W`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'scm_w') }}
                      placeholder={'Enter SCM (Fuel Model W)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('SCM')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 1000
                        }
                      }}
                    />
                    
                  </Grid>
                }

                {
                  /****** FUEL MODEL X SCM*/
                  fuelModels.includes('X') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSCM-X`} htmlFor={`createUpdateSCM-X`}>SCM (Fuel Model X)*</label>
                    <TextField
                      value={values.scm_x}
                      onBlur={() => setFieldTouched('scm_x')}
                      error={!!errors.scm_x && !!touched.scm_x}
                      helperText={errors.scm_x && touched.scm_x ? errors.scm_x : ''}
                      id={`createUpdateSCM-X`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'scm_x') }}
                      placeholder={'Enter SCM (Fuel Model X)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('SCM')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 1000
                        }
                      }}
                    />
                    
                  </Grid>
                }
                {
                  /****** FUEL MODEL Y SCM*/
                  fuelModels.includes('Y') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSCM-Y`} htmlFor={`createUpdateSCM-Y`}>SCM (Fuel Model Y)*</label>
                    <TextField
                      value={values.scm_y}
                      onBlur={() => setFieldTouched('scm_y')}
                      error={!!errors.scm_y && !!touched.scm_y}
                      helperText={errors.scm_y && touched.scm_y ? errors.scm_y : ''}
                      id={`createUpdateSCM-Y`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'scm_y') }}
                      placeholder={'Enter SCM (Fuel Model Y)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('SCM')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 1000
                        }
                      }}
                    />
                    
                  </Grid>
                }
                {
                  /****** FUEL MODEL Z SCM*/
                  fuelModels.includes('Z') &&
                  <Grid row desktop={{ col: 6 }}>
                    <label id={`createUpdateSCM-Z`} htmlFor={`createUpdateSCM-Z`}>SCM (Fuel Model Z)*</label>
                    <TextField
                      value={values.scm_z}
                      onBlur={() => setFieldTouched('scm_z')}
                      error={!!errors.scm_z && !!touched.scm_z}
                      helperText={errors.scm_z && touched.scm_z ? errors.scm_z : ''}
                      id={`createUpdateSCM-Z`}
                      type={'string'}
                      size='medium'
                      fullWidth
                      onChange={(e: any) => {setIntValue(e, 'scm_z') }}
                      placeholder={'Enter SCM (Fuel Model Z)'}
                      onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                      disabled={!fuelIndividualChange.includes('SCM')}

                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 1000
                        }
                      }}
                    />
                    
                  </Grid>
                }
                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
{/************************* DROUGHT CATALOG */}               
              <div style={{ width: '30%', marginRight: 0, marginLeft: 'auto', marginBottom: '1.5rem'}}>
                <Button
                  variant='contained'
                  onClick={()=> {setOpenDroughtModal(true)}}
                >
                  Create New Drought Catalog</Button>
              </div>
                {
                  !fuelIndividualChange.includes('Drought Catalog') && 
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-drought_id`} htmlFor={`createUpdateFuel-drought_id`}>Drought Catalog ({fuelModels?.filter(item=>item!=='All').join(', ')})*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('drought_id')}
                        id={`createUpdateFuel-drought_id`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'drought_id', 'id', false)
                        }}
                        value={droughtOptions?.find(option => option.id.toString() === values.drought_id?.toString()) || null}
                        options={droughtOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.name}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.name}-drought-selected`} value={option} id={option.id}>
                                {option.name}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Drought Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={ !!errors.drought_id
                              && !!touched.drought_id
                            }
                            helperText={ !!errors.drought_id
                              && !!touched.drought_id ? errors.drought_id:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL V DROUGHT */
                  fuelModels.includes('V') && fuelIndividualChange.includes('Drought Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-drought_id_v`} htmlFor={`createUpdateFuel-drought_id_v`}>Drought Catalog (Fuel Model V)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('drought_id_v')}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'drought_id_v', 'id', true)
                        }}
                        id={`createUpdateFuel-drought_id_v`}
                        style={{ width: '100%' }}
                        value={droughtOptions?.find(option => option.id.toString() === values.drought_id_v?.toString()) || null}
                        options={droughtOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.name}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.name}-drought-v`} value={option} id={option.id}>
                                {option.name}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Drought Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={ !!errors.drought_id_v
                              && !!touched.drought_id_v
                            }
                            helperText={ !!errors.drought_id_v
                              && !!touched.drought_id_v ? errors.drought_id_v:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL W DROUGHT */
                  fuelModels.includes('W') && fuelIndividualChange.includes('Drought Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-drought_id_w`} htmlFor={`createUpdateFuel-drought_id_w`}>Drought Catalog (Fuel Model W)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('drought_id_w')}
                        id={`createUpdateFuel-drought_id_w`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'drought_id_w', 'id', true)
                        }}
                        value={droughtOptions?.find(option => option.id.toString() === values.drought_id_w?.toString()) || null}
                        options={droughtOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.name}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.name}-drought-w`} value={option} id={option.id}>
                                {option.name}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Drought Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.drought_id_w
                              && !!touched.drought_id_w
                            }
                            helperText={ !!errors.drought_id_w
                              &&!!touched.drought_id_w ? errors.drought_id_w:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }

                {
                  /**FUEL MODEL X DROUGHT */
                  fuelModels.includes('X') && fuelIndividualChange.includes('Drought Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-drought_id_x`} htmlFor={`createUpdateFuel-drought_id_x`}>Drought Catalog (Fuel Model X)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('drought_id_x')}
                        id={`createUpdateFuel-drought_id_x`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'drought_id_x', 'id', true)
                        }}
                        value={droughtOptions?.find(option => option.id.toString() === values.drought_id_x?.toString()) || null}
                        options={droughtOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.name}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.name}-drought-x`} value={option} id={option.id}>
                                {option.name}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Drought Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={ !!errors.drought_id_x
                              && !!touched.drought_id_x
                            }
                            helperText={!!errors.drought_id_x
                              && !!touched.drought_id_x ? errors.drought_id_x:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL Y DROUGHT */
                  fuelModels.includes('Y') && fuelIndividualChange.includes('Drought Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-drought_id_y`} htmlFor={`createUpdateFuel-drought_id_y`}>Drought Catalog (Fuel Model Y)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('drought_id_y')}
                        id={`createUpdateFuel-drought_id_y`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'drought_id_y', 'id', true)
                        }}
                        value={droughtOptions?.find(option => option.id.toString() === values.drought_id_y?.toString()) || null}
                        options={droughtOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.name}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.name}-drought-y`} value={option} id={option.id}>
                                {option.name}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Drought Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.drought_id_y
                               && !!touched.drought_id_y
                            }
                            helperText={ !!errors.drought_id_y
                              && !!touched.drought_id_y ? errors.drought_id_y:''
                            }
                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL Z DROUGHT */
                  fuelModels.includes('Z') && fuelIndividualChange.includes('Drought Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-drought_id_z`} htmlFor={`createUpdateFuel-drought_id_z`}>Drought Catalog (Fuel Model Z)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('drought_id_z')}
                        id={`createUpdateFuel-drought_id_z`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'drought_id_z', 'id', true)
                        }}
                        value={droughtOptions?.find(option => option.id.toString() === values.drought_id_z?.toString()) || null}
                        options={droughtOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.name}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.name}-drought-z`} value={option} id={option.id}>
                                {option.name}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Drought Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.drought_id_z
                              && !!touched.drought_id_z
                            }
                            helperText={!!errors.drought_id_z
                              && !!touched.drought_id_z ? errors.drought_id_z:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }

                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
{/************************* DEAD FUEL CATALOG */}               
              <div style={{ width: '30%', marginRight: 0, marginLeft: 'auto', marginBottom: '1.5rem'}}>
                <Button
                  variant='contained'
                  onClick={()=> setOpenDeadModal(true)}
                >
                  Create New Dead Fuel Catalog</Button>
              </div>
                {
                  /**DEAD FUEL CATALOG */
                   !fuelIndividualChange.includes('Dead Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-dead_id`} htmlFor={`createUpdateFuel-dead_id`}>Dead Catalog ({fuelModels?.filter(item=>item!=='All').join(', ')})*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('dead_id')}
                        id={`createUpdateFuel-dead_id`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'dead_id', 'id', false)
                        }}
                        value={deadOptions?.find(option => option.id.toString() === values.dead_id?.toString()) || null}
                        options={deadOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.catalogName}-drought-z`} value={option} id={option.id}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Dead Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={ !!errors.dead_id
                              && !!touched.dead_id
                            }
                            helperText={!!errors.dead_id
                              && !!touched.dead_id ? errors.dead_id:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL V Dead */
                  fuelModels.includes('V') && fuelIndividualChange.includes('Dead Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-dead_id_v`} htmlFor={`createUpdateFuel-dead_id_v`}>Dead Catalog (Fuel Model V)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('dead_id_v')}
                        id={`createUpdateFuel-dead_id_v`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'dead_id_v', 'id', true)
                        }}
                        value={deadOptions?.find(option => option.id.toString() === values.dead_id_v?.toString()) || null}
                        options={deadOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.catalogName}-drought-z`} value={option} id={option.id}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Dead Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={ !!errors.dead_id_v
                              && !!touched.dead_id_v
                            }
                            helperText={ !!errors.dead_id_v
                              && !!touched.dead_id_v ? errors.dead_id_v:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL W Dead */
                  fuelModels.includes('W') && fuelIndividualChange.includes('Dead Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-dead_id_w`} htmlFor={`createUpdateFuel-dead_id_w`}>Dead Catalog (Fuel Model W)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('dead_id_w')}
                        id={`createUpdateFuel-dead_id_w`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'dead_id_w', 'id', true)
                        }}
                        value={deadOptions?.find(option => option.id.toString() === values.dead_id_w?.toString()) || null}
                        options={deadOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.catalogName}-drought-z`} value={option} id={option.id}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Dead Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.dead_id_w
                              && !!touched.dead_id_w
                            }
                            helperText={!!errors.dead_id_w
                              && !!touched.dead_id_w ? errors.dead_id_w:''
                            }


                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }

                {
                  /**FUEL MODEL X Dead */
                  fuelModels.includes('X') && fuelIndividualChange.includes('Dead Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-dead_id_x`} htmlFor={`createUpdateFuel-dead_id_x`}>Dead Catalog (Fuel Model X)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('dead_id_x')}
                        id={`createUpdateFuel-dead_id_x`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'dead_id_x', 'id', true)
                        }}
                        value={deadOptions?.find(option => option.id.toString() === values.dead_id_x?.toString()) || null}
                        options={deadOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.catalogName}-drought-z`} value={option} id={option.id}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Dead Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={ !!errors.dead_id_x
                              && !!touched.dead_id_x
                            }
                            helperText={!!errors.dead_id_x
                              && !!touched.dead_id_x ? errors.dead_id_x:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL Y Dead */
                  fuelModels.includes('Y') && fuelIndividualChange.includes('Dead Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-dead_id_y`} htmlFor={`createUpdateFuel-dead_id_y`}>Dead Catalog (Fuel Model Y)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('dead_id_y')}
                        id={`createUpdateFuel-dead_id_y`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'dead_id_y', 'id', true)
                        }}
                        value={deadOptions?.find(option => option.id.toString() === values.dead_id_y?.toString()) || null}
                        options={deadOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.catalogName}-drought-z`} value={option} id={option.id}>
                                {option.catalogName}
                              </li>
                        )}

                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Dead Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.dead_id_y
                              && !!touched.dead_id_y
                            }
                            helperText={!!errors.dead_id_y
                              && !!touched.dead_id_y ? errors.dead_id_y:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL Z Dead */
                  fuelModels.includes('Z') && fuelIndividualChange.includes('Dead Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-dead_id_z`} htmlFor={`createUpdateFuel-dead_id_z`}>Dead Catalog (Fuel Model Z)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('dead_id_z')}
                        id={`createUpdateFuel-dead_id_z`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'dead_id_z', 'id', true)
                        }}
                        value={deadOptions?.find(option => option.id.toString() === values.dead_id_z?.toString()) || null}
                        options={deadOptions}                        
                        isOptionEqualToValue={(option,value)=> option.id.toString()===value.id.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.id}-${option.catalogName}-drought-z`} value={option} id={option.id}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Dead Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.dead_id_z
                              && !!touched.dead_id_z
                            }
                            helperText={!!errors.dead_id_z
                              && !!touched.dead_id_z ? errors.dead_id_z:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }

                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
{/************************* LIVE FUEL CATALOG */}               
              <div style={{ width: '30%', marginRight: 0, marginLeft: 'auto', marginBottom: '1.5rem'}}>
                <Button
                  variant='contained'
                  onClick={()=> {setOpenLiveModal(true)}}
                >
                  Create New Live Fuel Catalog</Button>
              </div>
              {
                  /*Live */
                  !fuelIndividualChange.includes('Live Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-live_catalog_id`} htmlFor={`createUpdateFuel-live_catalog_id`}>Live Fuel Catalog ({fuelModels?.filter(item=>item!=='All').join(', ')})*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('live_catalog_id')}
                        id={`createUpdateFuel-live_catalog_id`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'live_catalog_id', 'gsiParameterCatalogId', false)
                        }}
                        value={liveOptions?.find(option => option.gsiParameterCatalogId.toString() === values.live_catalog_id?.toString()) || null}
                        options={liveOptions}                        
                        isOptionEqualToValue={(option,value)=> option.gsiParameterCatalogId.toString()===value.gsiParameterCatalogId.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.gsiParameterCatalogId}-${option.catalogName}-drought-z`} value={option} id={option.gsiParameterCatalogId}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Live Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.live_catalog_id
                              && !!touched.live_catalog_id
                            }
                            helperText={!!errors.live_catalog_id
                              && !!touched.live_catalog_id ? errors.live_catalog_id:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL V Live */
                  fuelModels.includes('V') && fuelIndividualChange.includes('Live Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-live_catalog_id_v`} htmlFor={`createUpdateFuel-live_catalog_id_v`}>Live Fuel Catalog (Fuel Model V)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('live_catalog_id_v')}
                        id={`createUpdateFuel-live_catalog_id_v`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'live_catalog_id_v', 'gsiParameterCatalogId', true)
                        }}
                        value={liveOptions?.find(option => option.gsiParameterCatalogId.toString() === values.live_catalog_id_v?.toString()) || null}
                        options={liveOptions}                        
                        isOptionEqualToValue={(option,value)=> option.gsiParameterCatalogId.toString()===value.gsiParameterCatalogId.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.gsiParameterCatalogId}-${option.catalogName}-drought-z`} value={option} id={option.gsiParameterCatalogId}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Live Fuel Catalogs'
                            //onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.live_catalog_id_v
                              && !!touched.live_catalog_id_v
                            }
                            helperText={!!errors.live_catalog_id_v
                              && !!touched.live_catalog_id_v ? errors.live_catalog_id_v:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL W LIVE FUEL */
                  fuelModels.includes('W') && fuelIndividualChange.includes('Live Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-live_catalog_id_w`} htmlFor={`createUpdateFuel-live_catalog_id_w`}>Live Fuel Catalog (Fuel Model W)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('live_catalog_id_w')}
                        id={`createUpdateFuel-live_catalog_id_w`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'live_catalog_id_w', 'gsiParameterCatalogId', true)
                        }}
                        value={liveOptions?.find(option => option.gsiParameterCatalogId.toString() === values.live_catalog_id_w?.toString()) || null}
                        options={liveOptions}                        
                        isOptionEqualToValue={(option,value)=> option.gsiParameterCatalogId.toString()===value.gsiParameterCatalogId.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.gsiParameterCatalogId}-${option.catalogName}-drought-z`} value={option} id={option.gsiParameterCatalogId}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Live Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.live_catalog_id_w
                              && !!touched.live_catalog_id_w
                            }
                            helperText={!!errors.live_catalog_id_w
                              && !!touched.live_catalog_id_w ? errors.live_catalog_id_w:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }

                {
                  /**FUEL MODEL X LIVE FUEL */
                  fuelModels.includes('X') && fuelIndividualChange.includes('Live Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-live_catalog_id_x`} htmlFor={`createUpdateFuel-live_catalog_id_x`}>Live Fuel Catalog (Fuel Model X)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('live_catalog_id_x')}
                        id={`createUpdateFuel-live_catalog_id_x`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'live_catalog_id_x', 'gsiParameterCatalogId', true)
                        }}
                        value={liveOptions?.find(option => option.gsiParameterCatalogId.toString() === values.live_catalog_id_x?.toString()) || null}
                        options={liveOptions}                        
                        isOptionEqualToValue={(option,value)=> option.gsiParameterCatalogId.toString()===value.gsiParameterCatalogId.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.gsiParameterCatalogId}-${option.catalogName}-drought-z`} value={option} id={option.gsiParameterCatalogId}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Live Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.live_catalog_id_x
                              &&!!touched.live_catalog_id_x
                            }
                            helperText={!!errors.live_catalog_id_x
                              && !!touched.live_catalog_id_x ? errors.live_catalog_id_x:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL Y LIVE FUEL */
                  fuelModels.includes('Y') && fuelIndividualChange.includes('Live Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-live_catalog_id_y`} htmlFor={`createUpdateFuel-live_catalog_id_y`}>Live Fuel Catalog (Fuel Model Y)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('live_catalog_id_y')}
                        id={`createUpdateFuel-live_catalog_id_y`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'live_catalog_id_y', 'gsiParameterCatalogId', true)
                        }}
                        value={liveOptions?.find(option => option.gsiParameterCatalogId.toString() === values.live_catalog_id_y?.toString()) || null}
                        options={liveOptions}                        
                        isOptionEqualToValue={(option,value)=> option.gsiParameterCatalogId.toString()===value.gsiParameterCatalogId.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.gsiParameterCatalogId}-${option.catalogName}-drought-z`} value={option} id={option.gsiParameterCatalogId}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Live Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.live_catalog_id_y && !!touched.live_catalog_id_y }
                            helperText={!!errors.live_catalog_id_y
                              && !!touched.live_catalog_id_y  ? errors.live_catalog_id_y:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }
                {
                  /**FUEL MODEL Z LIVE FUEL */
                  fuelModels.includes('Z') && fuelIndividualChange.includes('Live Fuel Catalog') &&
                  <Grid desktop={{ col: 7 }}>
                    <label id={`createUpdateFuel-live_catalog_id_z`} htmlFor={`createUpdateFuel-live_catalog_id_z`}>Live Fuel Catalog (Fuel Model Z)*</label>
                    <div className='searchAreas' style={{ width: '100%' }}>
                      <i className="fas fa-search" />
                      <Autocomplete
                        onBlur={() => setFieldTouched('live_catalog_id_z')}
                        id={`createUpdateFuel-live_catalog_id_z`}
                        style={{ width: '100%' }}
                        onChange={(event: any, newValue: any) => {
                          handleCatalogSelection(newValue, 'live_catalog_id_z', 'gsiParameterCatalogId', true)
                        }}
                        value={liveOptions?.find(option => option.gsiParameterCatalogId.toString() === values.live_catalog_id_z?.toString()) || null}
                        options={liveOptions}                        
                        isOptionEqualToValue={(option,value)=> option.gsiParameterCatalogId.toString()===value.gsiParameterCatalogId.toString()} 
                        getOptionLabel={(option)=> option.catalogName}
                        renderOption={
                          (props, option) => (
                              <li {...props} key={`${option.gsiParameterCatalogId}-${option.catalogName}-drought-z`} value={option} id={option.gsiParameterCatalogId}>
                                {option.catalogName}
                              </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder='Search Live Fuel Catalogs'
                            style={{ width: '100%' }}
                            onBlur={handleBlur}
                            error={!!errors.live_catalog_id_z
                              && !!touched.live_catalog_id_z
                            }
                            helperText={!!errors.live_catalog_id_z
                              && !!touched.live_catalog_id_z ? errors.live_catalog_id_z:''
                            }

                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              type: 'search',
                            }}
                          />
                        )}
                      />
                    </div>

                  </Grid>
                }

                { fuelModels?.length>0 && <Divider style={{marginTop: '2rem', marginBottom: '2rem'}}/>}
                </GridContainer>
            </Form>
          )
        }}
      </Formik>
    </GridContainer>
  )
}